import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from './env';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

export const api = axios.create({
  baseURL: env.VITE_API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

api.interceptors.request.use((config) => {
  const method = config.method?.toUpperCase();
  if (method && method !== 'GET' && method !== 'HEAD') {
    const csrf = readCookie('__Host-nt_csrf');
    if (csrf) config.headers.set('X-CSRF-Token', csrf);
  }
  return config;
});

let refreshPromise: Promise<void> | null = null;
function tryRefresh(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${env.VITE_API_URL}/auth/refresh`, null, { withCredentials: true })
      .then(() => undefined)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig | undefined;
    const url = config?.url ?? '';
    // O logout NÃO entra aqui: quando o access expira, a api devolve 401 e é o
    // reauth silencioso que o torna alcançável — sem ele, sair virava um beco sem
    // saída (a rota exige sessão viva, e nenhuma outra tela destrava). Se o refresh
    // também estiver morto, o catch abaixo leva ao login, que é o destino do clique.
    const skipRefresh = url.endsWith('/auth/login');
    if (error.response?.status === 401 && config && !config._retry && !skipRefresh) {
      config._retry = true;
      try {
        await tryRefresh();
        return api.request(config);
      } catch {
        // Já em /login não redireciona: o probe `GET /me` responde 401 quando deslogado e, sem
        // esta guarda, o replace recarrega /login em loop. Fora de /login, manda pro login.
        if (window.location.pathname !== '/login') {
          // Quem clicou em "Sair" não quer voltar para onde estava no próximo login.
          const from = url.endsWith('/auth/logout')
            ? ''
            : `?from=${encodeURIComponent(window.location.pathname)}`;
          window.location.replace(`/login${from}`);
        }
      }
    }
    return Promise.reject(error);
  },
);
